/* SEASON STANDINGS — the live 2355 league table. Reads the league-best scores
   every franchise has set in the training arena (/api/riotball/records), sorts
   the table, marks the playoff cut, highlights your aligned Titan Tag franchise,
   and refreshes live when a new record is posted on this page. */
import { Crown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SectionHeading } from "./SectionHeading";
import { API, TEAM_ORDER, TEAMS } from "./riotball/constants";
import { fetchMe, getCachedUser } from "../utils/auth";

/* game keys → dossier route ids (three older keys predate the dossier naming) */
const DOSSIER_ID = { red: "red-force", white: "white-guards", vultures: "voltaic-vultures" };
const dossierId = (key) => DOSSIER_ID[key] || key;

const PLAYOFF_CUT = 4; // top four make the Championship Playoff bracket

export function SeasonStandings() {
  const [records, setRecords] = useState(null);
  const [champions, setChampions] = useState([]);
  const [user, setUser] = useState(() => getCachedUser());

  useEffect(() => {
    fetchMe().then(setUser).catch(() => {});
    const onAuth = (e) => setUser(e.detail?.user ?? null);
    window.addEventListener("dolo-auth", onAuth);
    return () => window.removeEventListener("dolo-auth", onAuth);
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetch(`${API}/riotball/records`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (alive && data) setRecords(data);
        })
        .catch(() => {});
    load();
    const interval = window.setInterval(load, 30000);
    // a fresh record on this page re-polls the desk immediately
    window.addEventListener("riotball-records-changed", load);
    return () => {
      alive = false;
      window.clearInterval(interval);
      window.removeEventListener("riotball-records-changed", load);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/riotball/champions`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (alive && data) setChampions(data.champions || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const table = useMemo(
    () =>
      TEAM_ORDER.map((key) => ({
        key,
        ...TEAMS[key],
        best: records ? records[key] || 0 : null,
      })).sort((a, b) => (b.best ?? -1) - (a.best ?? -1)),
    [records],
  );

  const aligned = user?.franchise || null; // dossier-id form

  return (
    <section className="season-standings" data-testid="season-standings">
      <SectionHeading
        eyebrow="SEASON STANDINGS // LIVE TABLE"
        title="The league, as it stands."
        text="Every league-best score set in the training arena, ranked live. Top four make the Championship Playoff bracket — the rest fight the dust."
      />
      <div className="standings-table" data-testid="standings-table">
        <div className="standings-head" aria-hidden="true">
          <span>RANK</span>
          <span>FRANCHISE</span>
          <span>TRAIT</span>
          <span>LEAGUE BEST</span>
        </div>
        {table.map((team, i) => {
          const rank = i + 1;
          const isLeader = rank === 1 && (team.best ?? 0) > 0;
          const isYours = aligned === dossierId(team.key);
          const cutAfter = rank === PLAYOFF_CUT;
          return (
            <div className="standings-row-wrap" key={team.key}>
              <Link
                to={`/riotball/team/${dossierId(team.key)}`}
                className={`standings-row${isLeader ? " standings-leader" : ""}${isYours ? " standings-yours" : ""}`}
                style={{ "--team-color": team.color }}
                data-testid={`standings-row-${team.key}`}
              >
                <span className="standings-rank">
                  {isLeader ? <Crown size={13} /> : null}
                  {String(rank).padStart(2, "0")}
                </span>
                <span className="standings-club">
                  <img src={team.emblem} alt={`${team.name} crest`} loading="lazy" />
                  <strong>{team.name}</strong>
                </span>
                <span className="standings-trait">{team.trait.name}</span>
                <span className="standings-best" data-testid={`standings-best-${team.key}`}>
                  {team.best === null ? "—" : team.best}
                  <small>{team.best === 1 ? " GOAL" : " GOALS"}</small>
                </span>
                {isYours ? <span className="standings-you">YOUR FRANCHISE</span> : null}
              </Link>
              {cutAfter ? (
                <div className="standings-cut" data-testid="standings-playoff-cut">
                  <span>CHAMPIONSHIP PLAYOFF CUT — TOP {PLAYOFF_CUT}</span>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <p className="standings-foot">Table updates live — set a league best in the arena above and watch the board move.</p>
      {champions.length ? (
        <div className="hall-of-champions" data-testid="hall-of-champions">
          <span className="hall-title">
            <Crown size={12} /> HALL OF CHAMPIONS — CROWNED EVERY SUNDAY
          </span>
          <div className="hall-entries">
            {champions.map((c) => {
              const team = TEAMS[c.team];
              return (
                <span className="hall-entry" key={c.week} data-testid={`hall-entry-${c.week}`} style={team ? { "--team-color": team.color } : undefined}>
                  <em>{c.week}</em>
                  <strong style={team ? { color: team.color } : undefined}>{team ? team.name : c.team.toUpperCase()}</strong>
                  <span>{c.best} {c.best === 1 ? "GOAL" : "GOALS"}</span>
                </span>
              );
            })}
          </div>
          <p className="hall-note">Aligned runners collect +100 PTS when their franchise takes the crown.</p>
          <Link to="/champions" className="champions-play-link" data-testid="standings-hall-link">NIGHT CROWNS LIVE IN THE HALL TOO →</Link>
        </div>
      ) : null}
    </section>
  );
}
